import { Box, Container, Typography, Divider, Link } from '@mui/material';

const BRAND = 'Fitistic';
const EMAIL = 'info@yuviso.com';
const EFFECTIVE = 'April 17, 2026';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography variant="body1" sx={{ mb: 1.5, color: 'text.primary', lineHeight: 1.8 }}>
    {children}
  </Typography>
);

const Li = ({ children }) => (
  <Typography
    component="li"
    variant="body1"
    sx={{ mb: 0.75, color: 'text.primary', lineHeight: 1.8, ml: 2 }}
  >
    {children}
  </Typography>
);

export default function PrivacyPolicy() {
  return (
    <Box sx={{ bgcolor: '#F4F4F8', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{ color: '#1A1A1A', letterSpacing: '-0.03em', mb: 1 }}
          >
            {BRAND}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Effective date: {EFFECTIVE}
          </Typography>
        </Box>

        {/* Card */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: { xs: 3, md: 5 },
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          <P>
            {BRAND} ("we", "our", or "us") is committed to protecting your privacy. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your information when you
            use our mobile application and related services (collectively, the "Service"). Please
            read this policy carefully. If you disagree with its terms, please discontinue use of
            the Service.
          </P>

          <Divider sx={{ my: 3 }} />

          <Section title="1. Information We Collect">
            <P>We collect the following categories of information:</P>
            <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
              <Li>
                <strong>Account information</strong> — name, email address, and password when you
                register.
              </Li>
              <Li>
                <strong>Health & fitness data</strong> — height, weight, age, gender, activity
                level, fitness goal, BMI, workout sessions, rep counts, calories burned, and weight
                logs that you voluntarily enter.
              </Li>
              <Li>
                <strong>Nutrition data</strong> — meal logs, food items, and water intake that you
                record.
              </Li>
              <Li>
                <strong>Gym attendance</strong> — check-in timestamps recorded when you scan a gym
                QR code.
              </Li>
              <Li>
                <strong>Device information</strong> — device type, operating system version, and
                app version for crash reporting and compatibility.
              </Li>
              <Li>
                <strong>Camera</strong> — used solely to scan gym QR codes for check-in. We do not
                store, transmit, or process any camera images or video.
              </Li>
              <Li>
                <strong>Usage data</strong> — features used, session duration, and in-app
                interactions to improve the Service.
              </Li>
            </Box>
          </Section>

          <Section title="2. How We Use Your Information">
            <P>We use the information we collect to:</P>
            <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
              <Li>Create and manage your account.</Li>
              <Li>Provide personalised workout plans, meal plans, and fitness tracking.</Li>
              <Li>Calculate BMI, calorie estimates, and progress metrics.</Li>
              <Li>Record gym attendance when you scan a QR code.</Li>
              <Li>Send workout reminders, water intake alerts, and other notifications you configure.</Li>
              <Li>Sync your data across devices via our secure servers.</Li>
              <Li>Improve, maintain, and troubleshoot the Service.</Li>
              <Li>Respond to your support requests.</Li>
            </Box>
            <P>
              We do not sell your personal information to third parties. We do not use your health
              data for advertising purposes.
            </P>
          </Section>

          <Section title="3. Camera Permission">
            <P>
              The app requests camera access exclusively to scan QR codes displayed at your gym for
              check-in purposes. The camera feed is processed entirely on-device by the QR scanning
              library. No images, video, or camera data are stored on our servers or shared with
              any third party.
            </P>
          </Section>

          <Section title="4. Data Storage & Security">
            <P>
              Your data is stored in two places:
            </P>
            <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
              <Li>
                <strong>On your device</strong> — in an encrypted local SQLite database for
                offline access.
              </Li>
              <Li>
                <strong>On our servers</strong> — in a secured MongoDB database hosted on MongoDB
                Atlas with encryption at rest and in transit (TLS/HTTPS).
              </Li>
            </Box>
            <P>
              We implement industry-standard security measures including hashed passwords
              (bcrypt), JWT authentication, and HTTPS for all API communication. However, no
              method of transmission over the internet is 100% secure, and we cannot guarantee
              absolute security.
            </P>
          </Section>

          <Section title="5. Data Sharing">
            <P>We may share your information only in the following limited circumstances:</P>
            <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
              <Li>
                <strong>Gym administrators</strong> — your name and check-in timestamps are
                visible to the admin of the gym you are a member of.
              </Li>
              <Li>
                <strong>Trainers</strong> — if your gym assigns a trainer to your account, they
                may view your fitness profile and progress.
              </Li>
              <Li>
                <strong>Service providers</strong> — we use Firebase (Google) for push
                notifications and MongoDB Atlas for database hosting. These providers process data
                on our behalf under strict data processing agreements.
              </Li>
              <Li>
                <strong>Legal requirements</strong> — we may disclose information if required by
                law or to protect the rights and safety of our users.
              </Li>
            </Box>
          </Section>

          <Section title="6. Push Notifications">
            <P>
              With your permission, we send push notifications for workout reminders, water intake
              alerts, meal reminders, and inactivity nudges. You can disable notifications at any
              time in your device settings or within the app under Settings → Reminders &
              Notifications.
            </P>
          </Section>

          <Section title="7. Data Retention">
            <P>
              We retain your account data for as long as your account is active. If you delete
              your account, we will delete your personal data from our servers within 30 days,
              except where retention is required by law.
            </P>
            <P>
              <strong>Meal log retention:</strong> Meal and nutrition logs are subject to the
              following retention limits based on your account type:
            </P>
            <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
              <Li>
                <strong>Free accounts</strong> — meal logs are retained for <strong>30 days</strong>.
                Logs older than 30 days may be permanently deleted from our servers. Data within
                the last 30 days is always preserved.
              </Li>
              <Li>
                <strong>Premium accounts</strong> — meal logs are retained for the full duration
                of your active subscription with no 30-day limit.
              </Li>
            </Box>
            <P>
              We recommend upgrading to a Premium account if you need long-term access to your
              nutrition history.
            </P>
          </Section>

          <Section title="8. Children's Privacy">
            <P>
              The Service is not directed to children under the age of 13. We do not knowingly
              collect personal information from children under 13. If you believe a child has
              provided us with personal information, please contact us and we will delete it
              promptly.
            </P>
          </Section>

          <Section title="9. Your Rights">
            <P>Depending on your location, you may have the right to:</P>
            <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
              <Li>Access the personal data we hold about you.</Li>
              <Li>Correct inaccurate data via the Profile → Edit screen in the app.</Li>
              <Li>Request deletion of your account and associated data.</Li>
              <Li>Withdraw consent for notifications at any time.</Li>
            </Box>
            <P>
              To exercise these rights, contact us at{' '}
              <Link href={`mailto:${EMAIL}`} underline="hover">
                {EMAIL}
              </Link>
              .
            </P>
          </Section>

          <Section title="10. Third-Party Services">
            <P>The app integrates with the following third-party services:</P>
            <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
              <Li>
                <strong>Firebase (Google)</strong> — push notifications. Subject to{' '}
                <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener" underline="hover">
                  Google's Privacy Policy
                </Link>
                .
              </Li>
              <Li>
                <strong>Google Gemini AI</strong> — AI coach feature. Queries are sent to
                Google's API. Subject to{' '}
                <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener" underline="hover">
                  Google's Privacy Policy
                </Link>
                .
              </Li>
              <Li>
                <strong>MongoDB Atlas</strong> — cloud database hosting. Subject to{' '}
                <Link href="https://www.mongodb.com/legal/privacy-policy" target="_blank" rel="noopener" underline="hover">
                  MongoDB's Privacy Policy
                </Link>
                .
              </Li>
            </Box>
          </Section>

          <Section title="11. Changes to This Policy">
            <P>
              We may update this Privacy Policy from time to time. We will notify you of material
              changes by updating the effective date at the top of this page. Continued use of the
              Service after changes constitutes acceptance of the updated policy.
            </P>
          </Section>

          <Section title="12. Contact Us">
            <P>
              If you have questions or concerns about this Privacy Policy or our data practices,
              please contact us:
            </P>
            <Box
              sx={{
                bgcolor: '#F4F4F8',
                borderRadius: 2,
                p: 2.5,
                mt: 1,
              }}
            >
              <Typography variant="body1" fontWeight={700} sx={{ color: 'text.primary', mb: 0.5 }}>
                {BRAND}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Email:{' '}
                <Link href={`mailto:${EMAIL}`} underline="hover">
                  {EMAIL}
                </Link>
              </Typography>
            </Box>
          </Section>
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            © {new Date().getFullYear()} {BRAND}. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
